// =============================================
// AI Admin Tools — OpenAI Function Calling
// Admin-specific tools that query system-wide data.
// All tools are READ-ONLY except crear_incidencia_admin.
// =============================================

import { createClient } from '@supabase/supabase-js';
import { ASSISTANT_TOOLS, executeToolCall as executeAsesorTool } from './ai-tools';

const supabaseAdmin = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.SUPABASE_SERVICE_ROLE_KEY!,
  { auth: { autoRefreshToken: false, persistSession: false } }
);

type ToolResult = { success: true; data: unknown } | { success: false; error: string };

// ── Admin-specific tool definitions ──────────────────────────

const ADMIN_ONLY_TOOLS: Array<{
  type: 'function';
  function: { name: string; description: string; parameters: Record<string, unknown> };
}> = [
  {
    type: 'function',
    function: {
      name: 'estadisticas_sistema',
      description:
        'Devuelve estadísticas globales del sistema: clientes, documentos, incidencias, asesores, alertas y sincronización Cegid. Úsalo cuando el usuario pregunte por el estado general, métricas, KPIs o resumen global.',
      parameters: { type: 'object', properties: {}, required: [] },
    },
  },
  {
    type: 'function',
    function: {
      name: 'listar_asesores',
      description:
        'Lista todos los asesores del sistema con el número de clientes asignados. Úsalo cuando el usuario pregunte por el equipo, asesores activos o carga de trabajo.',
      parameters: { type: 'object', properties: {}, required: [] },
    },
  },
  {
    type: 'function',
    function: {
      name: 'estado_sync_cegid',
      description:
        'Muestra el historial y estado de las sincronizaciones con Cegid ERP. Úsalo cuando el usuario pregunte por la sync, la integración Cegid o errores de sincronización.',
      parameters: {
        type: 'object',
        properties: {
          limite: {
            type: 'number',
            description: 'Número de registros a devolver (por defecto 10)',
          },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'listar_alertas_sistema',
      description:
        'Lista las alertas del sistema: alertas activas, pendientes de revisión y recientes. Úsalo cuando el usuario pregunte por alertas, notificaciones o avisos.',
      parameters: {
        type: 'object',
        properties: {
          solo_pendientes: {
            type: 'boolean',
            description: 'Si true, devuelve solo las alertas no reconocidas',
          },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'resumen_financiero_global',
      description:
        'Devuelve el resumen financiero agregado de todos los clientes: ingresos, gastos, resultado, clientes en pérdidas. Úsalo cuando el usuario pregunte por la situación financiera global, cartera o resultados.',
      parameters: {
        type: 'object',
        properties: {
          periodo: {
            type: 'string',
            description: 'Período en formato YYYY-MM. Si no se especifica, usa el más reciente.',
          },
        },
        required: [],
      },
    },
  },
];

// ── Merge with shared asesor tools (re-using listar_clientes, buscar_documentos, listar_incidencias) ──
// Admin gets ALL tools, asesor tools already support admin via getClienteIdsForAsesor
export const ADMIN_TOOLS = [...ASSISTANT_TOOLS, ...ADMIN_ONLY_TOOLS];

// ── Admin tool context ────────────────────────────────────────

interface AdminToolContext {
  userId: string;
  userRole: 'admin';
}

// ── Dispatcher ───────────────────────────────────────────────

export async function executeAdminToolCall(
  toolName: string,
  args: Record<string, unknown>,
  context: AdminToolContext
): Promise<ToolResult> {
  try {
    // Admin-specific tools
    switch (toolName) {
      case 'estadisticas_sistema':
        return await ejecutarEstadisticasSistema();
      case 'listar_asesores':
        return await ejecutarListarAsesores();
      case 'estado_sync_cegid':
        return await ejecutarEstadoSyncCegid(args);
      case 'listar_alertas_sistema':
        return await ejecutarListarAlertasSistema(args);
      case 'resumen_financiero_global':
        return await ejecutarResumenFinancieroGlobal(args);
      // Delegate shared asesor tools (they already support admin role)
      default:
        return await executeAsesorTool(toolName, args, { userId: context.userId, userRole: 'admin' });
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error(`[ai-tools-admin] Error executing ${toolName}:`, message);
    return { success: false, error: message };
  }
}

// ── Tool implementations ──────────────────────────────────────

async function ejecutarEstadisticasSistema(): Promise<ToolResult> {
  const now = new Date();
  const inicioMes = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

  const [clientesRes, docsTotalRes, docsMesRes, incRes, asesoresRes, syncRes, alertsRes] =
    await Promise.all([
      supabaseAdmin.from('clientes').select('id, activo', { count: 'exact' }),
      supabaseAdmin.from('documentos').select('id, estado', { count: 'exact' }),
      supabaseAdmin
        .from('documentos')
        .select('id, estado', { count: 'exact' })
        .gte('fecha_subida', inicioMes),
      supabaseAdmin.from('incidencias').select('id, estado'),
      supabaseAdmin.from('profiles').select('id').eq('role', 'asesor'),
      supabaseAdmin
        .from('cegid_sync_runs')
        .select('id, status, started_at')
        .order('started_at', { ascending: false })
        .limit(1),
      supabaseAdmin
        .from('alert_history')
        .select('id, acknowledged', { count: 'exact' })
        .eq('acknowledged', false),
    ]);

  const clientes = clientesRes.data || [];
  const docsMes = docsMesRes.data || [];
  const incidencias = incRes.data || [];

  const mesActual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  return {
    success: true,
    data: {
      mes_referencia: mesActual,
      clientes: {
        total: clientesRes.count || clientes.length,
        activos: clientes.filter((c: any) => c.activo !== false).length,
      },
      documentos: {
        total_historico: docsTotalRes.count || 0,
        total_este_mes: docsMes.length,
        procesados_mes: docsMes.filter((d: any) =>
          ['procesado_ia', 'validado', 'contabilizado'].includes(d.estado)
        ).length,
        pendientes_mes: docsMes.filter((d: any) =>
          ['pendiente', 'incidencia'].includes(d.estado)
        ).length,
        tasa_exito_mes:
          docsMes.length > 0
            ? (
                (docsMes.filter((d: any) =>
                  ['procesado_ia', 'validado', 'contabilizado'].includes(d.estado)
                ).length /
                  docsMes.length) *
                100
              ).toFixed(1) + '%'
            : 'Sin datos',
      },
      incidencias: {
        abiertas: incidencias.filter((i: any) => i.estado === 'nueva').length,
        en_revision: incidencias.filter((i: any) => i.estado === 'en_revision').length,
        resueltas: incidencias.filter((i: any) =>
          ['resuelta', 'cerrada'].includes(i.estado)
        ).length,
      },
      asesores: { total: asesoresRes.data?.length || 0 },
      ultima_sync_cegid: syncRes.data?.[0] || null,
      alertas_pendientes: alertsRes.count || 0,
    },
  };
}

async function ejecutarListarAsesores(): Promise<ToolResult> {
  const { data: asesores, error } = await supabaseAdmin
    .from('profiles')
    .select('id, full_name, email')
    .eq('role', 'asesor')
    .order('full_name', { ascending: true });

  if (error) return { success: false, error: error.message };

  // Get client counts per asesor
  const asesorIds = (asesores || []).map((a: any) => a.id);
  const { data: asignaciones } = await supabaseAdmin
    .from('asesor_cliente')
    .select('asesor_id')
    .in('asesor_id', asesorIds);

  const countMap: Record<string, number> = {};
  for (const a of asignaciones || []) {
    countMap[a.asesor_id] = (countMap[a.asesor_id] || 0) + 1;
  }

  return {
    success: true,
    data: (asesores || []).map((a: any) => ({
      id: a.id,
      nombre: a.full_name,
      email: a.email,
      clientes_asignados: countMap[a.id] || 0,
    })),
  };
}

async function ejecutarEstadoSyncCegid(args: Record<string, unknown>): Promise<ToolResult> {
  const limite = typeof args.limite === 'number' ? Math.min(args.limite, 20) : 10;

  const { data: runs, error } = await supabaseAdmin
    .from('cegid_sync_runs')
    .select(
      'id, status, sync_type, started_at, completed_at, duration_ms, records_processed, records_success, records_failed, error_message'
    )
    .order('started_at', { ascending: false })
    .limit(limite);

  if (error) return { success: false, error: error.message };

  const { data: mappings } = await supabaseAdmin
    .from('cegid_company_mapping')
    .select('cegid_company_code, active, sync_enabled, tipo_opcion_fiscal');

  return {
    success: true,
    data: {
      runs: (runs || []).map((r: any) => ({
        id: r.id,
        estado: r.status,
        tipo: r.sync_type,
        inicio: r.started_at,
        fin: r.completed_at,
        duracion_seg: r.duration_ms ? (r.duration_ms / 1000).toFixed(1) : null,
        registros_procesados: r.records_processed,
        registros_exitosos: r.records_success,
        registros_fallidos: r.records_failed,
        error: r.error_message,
      })),
      mappings_activos: (mappings || []).filter((m: any) => m.active && m.sync_enabled),
    },
  };
}

async function ejecutarListarAlertasSistema(args: Record<string, unknown>): Promise<ToolResult> {
  const soloPendientes = args.solo_pendientes === true;

  let query = supabaseAdmin
    .from('alert_history')
    .select('id, rule_id, triggered_at, acknowledged, acknowledged_at, alert_rules(name, trigger_type, severity)')
    .order('triggered_at', { ascending: false })
    .limit(20);

  if (soloPendientes) {
    query = query.eq('acknowledged', false);
  }

  const { data, error } = await query;
  if (error) return { success: false, error: error.message };

  return {
    success: true,
    data: (data || []).map((a: any) => ({
      id: a.id,
      tipo: a.alert_rules?.trigger_type,
      nombre: a.alert_rules?.name,
      severidad: a.alert_rules?.severity,
      disparada: a.triggered_at,
      reconocida: a.acknowledged,
      reconocida_en: a.acknowledged_at,
    })),
  };
}

async function ejecutarResumenFinancieroGlobal(args: Record<string, unknown>): Promise<ToolResult> {
  let query = supabaseAdmin
    .from('datos_financieros')
    .select('cliente_id, periodo, ingresos, gastos, resultado, margen');

  const periodo = typeof args.periodo === 'string' ? args.periodo : null;

  if (periodo) {
    query = query.eq('periodo', periodo);
  } else {
    // Get latest period
    const { data: latest } = await supabaseAdmin
      .from('datos_financieros')
      .select('periodo')
      .order('periodo', { ascending: false })
      .limit(1);
    const latestPeriodo = latest?.[0]?.periodo;
    if (latestPeriodo) query = query.eq('periodo', latestPeriodo);
  }

  const { data, error } = await query;
  if (error) return { success: false, error: error.message };

  const rows = data || [];
  const periodoUsado = rows[0]?.periodo || periodo || 'Sin datos';

  const totalIngresos = rows.reduce((s: number, r: any) => s + (parseFloat(r.ingresos) || 0), 0);
  const totalGastos = rows.reduce((s: number, r: any) => s + (parseFloat(r.gastos) || 0), 0);
  const totalResultado = rows.reduce((s: number, r: any) => s + (parseFloat(r.resultado) || 0), 0);
  const clientesEnPerdidas = rows.filter((r: any) => parseFloat(r.resultado) < 0);

  const fmt = (n: number) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

  return {
    success: true,
    data: {
      periodo: periodoUsado,
      clientes_con_datos: rows.length,
      ingresos_totales: fmt(totalIngresos),
      gastos_totales: fmt(totalGastos),
      resultado_agregado: fmt(totalResultado),
      resultado_positivo: totalResultado >= 0,
      clientes_en_perdidas: clientesEnPerdidas.length,
      detalle_perdidas: clientesEnPerdidas.map((r: any) => ({
        cliente_id: r.cliente_id,
        resultado: fmt(parseFloat(r.resultado)),
      })),
    },
  };
}
